import { EnquiryFormShell, HoneypotField } from '@/components/forms';
import { consultationForm, CONSULTATION_WHATSAPP_MESSAGE } from '@/content/consultation';

import { ConsultationFields } from './ConsultationFields';
import { useConsultationForm } from './useConsultationForm';

type Props = {
  /**
   * Target of the enclosing Section's `aria-labelledby`, for a band whose
   * heading IS this form's heading. The band on /contact has a
   * `SectionHeading` of its own and passes nothing; the page that is only
   * this form hands its own id down, so the landmark is named by the h2 a
   * visitor can actually see rather than by a heading that is not there.
   */
  titleId?: string;
};

/**
 * §32 — the import-advice request. Not a booking and no longer shaped like
 * one: the site has no calendar and invents no availability (A16), so the form
 * asks where to send the answer rather than when to meet.
 *
 * All this component does is put the frame, the honeypot and the questions
 * together. The state and the submission are in `useConsultationForm`, the
 * questions are in `ConsultationFields` and the words are in
 * `content/consultation.ts`, so what is asked, what happens when it is sent
 * and what it is called can each change without touching the other two.
 *
 * Shared, so the two places that ask for advice are the same form and not two
 * forms that look alike. Neither caller may pass copy in: a page that could
 * retitle the form or relabel its button is a page that can promise something
 * the site cannot deliver — which is exactly how the old wording went wrong.
 */
export function ConsultationForm({ titleId }: Props) {
  const { control, register, onSubmit, isPending, error, receipt } = useConsultationForm();

  return (
    <EnquiryFormShell
      title={consultationForm.title}
      {...(titleId ? { titleId } : {})}
      description={consultationForm.description}
      submitLabel={consultationForm.submitLabel}
      onSubmit={onSubmit}
      isPending={isPending}
      error={error}
      receipt={receipt}
      whatsappMessage={CONSULTATION_WHATSAPP_MESSAGE}
    >
      <HoneypotField field={register('companyWebsite')} />
      <ConsultationFields control={control} />
    </EnquiryFormShell>
  );
}
